import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency, formatNumber } from '../../lib/utils';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import StatCard from '../../components/ui/StatCard';
import { Users, UserPlus, TrendingUp, Search, ChevronRight, ChevronDown } from 'lucide-react';

export default function AdminTeamPage() {
  const [searchUserId, setSearchUserId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(1);

  const { data: stats } = useQuery({
    queryKey: ['teamStats', targetUserId],
    queryFn: () => api.get(`/mlm/stats`).then(r => r.data),
    enabled: !!targetUserId,
  });

  const { data: levelMembers } = useQuery({
    queryKey: ['levelMembers', targetUserId, selectedLevel],
    queryFn: () => api.get(`/mlm/levels/${selectedLevel}`).then(r => r.data),
    enabled: !!targetUserId,
  });

  const handleSearch = () => { if (searchUserId) setTargetUserId(searchUserId); };

  return (
    <div className="space-y-6 animate-fade-in">
      <div><h1 className="text-2xl font-bold text-white">Team / MLM Management</h1><p className="text-sm text-slate-400">View and manage MLM genealogy</p></div>

      <Card><CardContent className="py-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Enter User ID to view their team..." value={searchUserId} onChange={e => setSearchUserId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full pl-9 pr-4 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.02] text-white placeholder-slate-500" />
          </div>
          <Button onClick={handleSearch}>Search</Button>
        </div>
      </CardContent></Card>

      {targetUserId && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard title="Team Size" value={formatNumber(stats?.totalTeamSize || 0)} icon={Users} />
            <StatCard title="Active Team" value={formatNumber(stats?.activeTeam || 0)} icon={TrendingUp} />
            <StatCard title="Direct Referrals" value={formatNumber(stats?.directReferrals || 0)} icon={UserPlus} />
            <StatCard title="Active Directs" value={formatNumber(stats?.activeDirects || 0)} icon={UserPlus} />
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white">Level Members</h2>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(l => (
                    <button key={l} onClick={() => setSelectedLevel(l)}
                      className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${selectedLevel === l ? 'bg-primary-600/15 text-primary-400' : 'bg-white/[0.04] text-slate-300 hover:bg-white/[0.06]'}`}>
                      L{l}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/[0.06] bg-white/[0.02]">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">User</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Joined</th>
                  </tr></thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {levelMembers?.length === 0 ? <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-500">No members at this level</td></tr> :
                      levelMembers?.map((m: any) => (
                        <tr key={m.id} className="hover:bg-white/[0.02]">
                          <td className="px-4 py-3"><p className="text-sm font-medium text-white">{m.user?.profile?.fullName || m.user?.username}</p><p className="text-xs text-slate-500">@{m.user?.username}</p></td>
                          <td className="px-4 py-3"><Badge status={m.user?.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                          <td className="px-4 py-3 text-sm text-slate-400">{new Date(m.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
